import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { changeDefaultPassword } from "src/api";
import { updatePassword } from "src/api/settings";
import { PasswordField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { showPromiseToast } from "src/lib/utils";

interface PasswordFormValues {
	current_password: string;
	new_password: string;
	new_password_confirmation: string;
}

const initialValues: PasswordFormValues = {
	current_password: "",
	new_password: "",
	new_password_confirmation: "",
};

const validationSchema = Yup.object().shape({
	current_password: Yup.string().required("Old password is required"),
	new_password: Yup.string().required("New password is required"),
	new_password_confirmation: Yup.string()
		.required("This field is required")
		.oneOf([Yup.ref("new_password")], "Passwords must match"),
});

interface UpdatePasswordProps {
	/** Set only by the forced default-password prompt, which posts to a different endpoint. */
	isForDefault?: boolean;
}

const UpdatePassword = ({ isForDefault }: UpdatePasswordProps) => {
	const { logout } = useAuth();
	const closePortal = usePortalContext();

	const { mutate, isLoading } = useMutation(isForDefault ? changeDefaultPassword : updatePassword, {
		onSuccess: ({ message }) => {
			closePortal();

			// Changing the password invalidates the session, so log out once the toast has been read.
			showPromiseToast(new Promise<void>((resolve) => setTimeout(() => resolve(logout()), 2500)), {
				loading: message,
				description: "You'll be logged out soon",
				success: () => ({ message: "Please login again" }),
				error: "Error",
			});
		},
	});

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				if (isForDefault) {
					mutate(values);
				} else {
					const { new_password_confirmation, ...rest } = values;
					mutate(rest);
				}
			}}
		>
			{({ values, handleChange, handleSubmit, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<PasswordField
						label="Current password"
						name="current_password"
						placeholder="Current password"
						autoComplete="current-password"
						value={values.current_password}
						error={touched.current_password && errors.current_password}
						onChange={handleChange}
						required
					/>
					<PasswordField
						label="New password"
						name="new_password"
						placeholder="New password"
						autoComplete="new-password"
						value={values.new_password}
						error={touched.new_password && errors.new_password}
						onChange={handleChange}
						required
					/>
					<PasswordField
						label="Confirm password"
						name="new_password_confirmation"
						placeholder="Confirm password"
						autoComplete="new-password"
						value={values.new_password_confirmation}
						error={touched.new_password_confirmation && errors.new_password_confirmation}
						onChange={handleChange}
						required
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Updating..." : "Update password"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default UpdatePassword;
